import type { ProfileConfig } from "@worker/config-schema";
import { el } from "../../lib/dom";
import { widgetShell, fetchWidget, formatCount } from "./base";

type YouTubeWidget = Extract<ProfileConfig["widgets"][number], { type: "youtube" }>;

type YouTubeData = {
  channelId: string;
  title?: string;
  thumbnail?: string;
  subscribers?: number;
  videoCount?: number;
  unconfigured?: boolean;
  notFound?: boolean;
};

export function mountYoutubeWidget(cfg: YouTubeWidget): HTMLElement {
  const shell = widgetShell({ kind: "youtube", title: "YouTube", accent: "#ff0000" });
  shell.setLoading();

  const controller = new AbortController();
  shell.root.addEventListener("bio:teardown", () => controller.abort());

  const render = async () => {
    const env = await fetchWidget<YouTubeData>(
      "youtube",
      { id: cfg.channelId },
      { signal: controller.signal },
    );
    if (!env.ok || !env.data) return shell.setError(env.error ?? "unavailable");
    const d = env.data;
    if (d.unconfigured) return shell.setError("requires YOUTUBE_API_KEY");
    if (d.notFound) return shell.setError("channel not found");
    shell.body.innerHTML = "";
    const row = el("a", {
      class: "widget-yt__row",
      href: `https://www.youtube.com/channel/${cfg.channelId}`,
      target: "_blank",
      rel: "noopener noreferrer",
    });
    if (d.thumbnail) {
      row.append(el("img", { class: "widget-yt__thumb", src: d.thumbnail, alt: "", draggable: false }));
    }
    const info = el("div", { class: "widget-yt__info" });
    info.append(el("div", { class: "widget-yt__name" }, [d.title ?? cfg.channelId]));
    info.append(
      el("div", { class: "widget-yt__stats" }, [
        `${formatCount(d.subscribers ?? 0)} subs · ${formatCount(d.videoCount ?? 0)} videos`,
      ]),
    );
    row.append(info);
    shell.body.append(row);
  };

  void render();
  const interval = window.setInterval(render, 10 * 60_000);
  shell.root.addEventListener("bio:teardown", () => window.clearInterval(interval));

  return shell.root;
}
